import React from "react";
import { useDiagramDataV2 } from "./hooks/useDiagramDataV2";

export const DiagramBasicV2 = () => {
  const data = useDiagramDataV2();
  const [scale, setScale] = React.useState(1);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Obtener el tamaño natural del contenido
      const contentHeight = container.scrollHeight;
      const contentWidth = container.scrollWidth;

      // Calcular escala basada en el 95% del alto
      const scaleByHeight = (viewportHeight * 0.95) / contentHeight;

      // Calcular el ancho que tendría con esa escala
      const projectedWidth = contentWidth * scaleByHeight;

      // Si el ancho proyectado supera el viewport, ajustar por ancho
      let finalScale = scaleByHeight;
      if (projectedWidth > viewportWidth * 0.95) {
        finalScale = (viewportWidth * 0.95) / contentWidth;
      }

      setScale(finalScale);
    };

    // Calcular escala inicial
    setTimeout(calculateScale, 100);

    // Recalcular en resize
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        backgroundColor: "#ffffff",
        padding: "10px",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          width: "fit-content",
          height: "fit-content",
          margin: "0 auto",
          alignItems: "flex-start",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          transition: "transform 0.3s ease",
        }}
      >

        {/* Main container */}
        <div
          style={{
            border: "2px solid #0b152F",
            borderRadius: "8px",
            backgroundColor: "#fff",
            padding: "20px",
            flex: 1,
            maxWidth: "4500px",
            minWidth: "1800px",
            position: "relative",
            height: "100%",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Resource group label */}
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                backgroundColor: "#416BA0",
                borderRadius: "4px",
              }}
            />
            <span style={{ color: "#000" }}>
              Solution
              <br />
              Ecosystem
            </span>
          </div>

          {/* Header with logo and title */}
          <div
            style={{
              padding: "10px",
              textAlign: "center",
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              minWidth: "200px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                padding: "15px 30px",
                backgroundColor: "transparent",
                border: data.logo ? `2px solid ${data.color1}` : "none",
                borderRadius: "8px",
              }}
            >
              {data.logo && (
                <img
                  src={data.logo}
                  alt={data.nombre}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#000",
                }}
              >
                {data.nombre}
              </h2>
            </div>
            {/* Arrows between logo/name and Veritran Workspace */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                fontSize: "20px",
                color: "#416BA0",
                fontWeight: "bold",
              }}
            >
              <span>↓</span>
              <span>↑</span>
            </div>
          </div>

          {/* Veritran Workspace / Middleware header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "15px",
              padding: "15px 10px",
              backgroundColor: "#416BA0",
              borderRadius: "6px",
              marginTop: "10px",
              marginBottom: "5px",
            }}
          >
            <img
              src="/veritranLogo.png"
              alt="Veritran"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "contain",
              }}
            />
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#fff",
              }}
            >
              Veritran Workspace / Middleware
            </h3>
          </div>

          {/* Arrows section below Veritran header */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "5px 0",
              marginBottom: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                fontSize: "20px",
                color: "#416BA0",
                fontWeight: "bold",
              }}
            >
              <span>↓</span>
              <span>↑</span>
            </div>
          </div>

          {/* Onboarding Process - Horizontal full width */}
          {data.onboardingSteps && data.onboardingSteps.length > 0 && (
            <div
              style={{
                border: "2px dashed #0b152F",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                padding: "15px",
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  padding: "6px",
                  backgroundColor: "#416BA0",
                  color: "#fff",
                  borderRadius: "4px",
                  textAlign: "center",
                }}
              >
                Onboarding Process
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {data.onboardingSteps.map((step, index) => (
                  <React.Fragment key={index}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "14px",
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        minHeight: "120px",
                        minWidth: "120px",
                        maxWidth: "140px",
                      }}
                    >
                      <img
                        src={step.image}
                        alt={step.label}
                        style={{
                          width: "65px",
                          height: "65px",
                          marginBottom: "8px",
                          objectFit: "contain",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          textAlign: "center",
                          color: "#333",
                        }}
                      >
                        {step.label}
                      </div>
                    </div>
                    {index < data.onboardingSteps.length - 1 && (
                      <div
                        style={{
                          fontSize: "18px",
                          color: data.color1,
                          fontWeight: "bold",
                        }}
                      >
                        →
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Arrows between Onboarding and two-column section */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              padding: "5px 0",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                fontSize: "20px",
                color: "#416BA0",
                fontWeight: "bold",
              }}
            >
              <span>↓</span>
              <span>↑</span>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                fontSize: "20px",
                color: "#416BA0",
                fontWeight: "bold",
              }}
            >
              <span>↓</span>
              <span>↑</span>
            </div>
          </div>

          {/* Two-column layout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flex: 1,
              minHeight: 0,
              gap: "20px",
            }}
          >
            {/* Left column - Enable Features */}
            <div style={{ display: "flex", flexDirection: "column", width: "48%" }}>
              {data.otherFeaturesItems && data.otherFeaturesItems.length > 0 && (
                <div
                  style={{
                    border: "2px dashed #0b152F",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                    padding: "15px",
                    flex: 1,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      padding: "6px",
                      backgroundColor: "#416BA0",
                      color: "#fff",
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    Enable Features
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "8px",
                      marginTop: "5px",
                    }}
                  >
                    {data.otherFeaturesItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "14px",
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                          minHeight: "120px",
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.label}
                          style={{
                            width: "65px",
                            height: "65px",
                            marginBottom: "8px",
                            objectFit: "contain",
                          }}
                        />
                        <div
                          style={{
                            fontSize: "12px",
                            textAlign: "center",
                            color: "#333",
                          }}
                        >
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Arrow block between columns */}
            <div
              style={{
                width: "4%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  fontSize: "24px",
                  color: "#416BA0",
                  fontWeight: "bold",
                }}
              >
                <span>→</span>
                <span>←</span>
              </div>
            </div>

            {/* Right column - Veritran Components + 3rd Parties */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "48%",
                gap: "15px",
              }}
            >
              {/* Veritran Components section */}
              <div
                style={{
                  border: "2px dashed #0b152F",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  padding: "15px",
                  flex: 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    padding: "6px",
                    backgroundColor: "#416BA0",
                    color: "#fff",
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Veritran Components
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "8px",
                    marginTop: "5px",
                    overflow: "auto",
                    flex: 1,
                  }}
                >
                  {data.identityItems.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        backgroundColor: "#fff",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        minHeight: "95px",
                      }}
                    >
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: item.color || "#0078D4",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "18px",
                          fontWeight: "bold",
                          marginBottom: "8px",
                        }}
                      >
                        {item.iconName.substring(0, 2).toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          textAlign: "center",
                          color: "#333",
                        }}
                      >
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrows between Veritran Components and 3rd Parties */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    fontSize: "20px",
                    color: "#416BA0",
                    fontWeight: "bold",
                  }}
                >
                  <span>↓</span>
                  <span>↑</span>
                </div>
              </div>

              {/* 3rd Parties involved section */}
              <div
                style={{
                  border: "2px dashed #0b152F",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  padding: "15px",
                  flex: 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    padding: "6px",
                    backgroundColor: "#416BA0",
                    color: "#fff",
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  3rd Parties involved
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "12px",
                    marginTop: "5px",
                    overflow: "auto",
                    flex: 1,
                    alignContent: "flex-start",
                  }}
                >
                  {data.systemHealthItems.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        backgroundColor: "#fff",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        minHeight: "70px",
                        maxHeight: "80px",
                      }}
                    >
                      <img
                        src={item.logoPath}
                        alt={item.name}
                        style={{
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          maxHeight: "50px",
                          maxWidth: "100%",
                        }}
                        onError={(e) => {
                          // Try jpg if png fails
                          if (e.target.src === item.logoPath && item.logoPathJpg) {
                            e.target.src = item.logoPathJpg;
                          } else if (e.target.src === item.logoPathJpg && item.logoPathWebp) {
                            // Try webp if jpg fails
                            e.target.src = item.logoPathWebp;
                          } else {
                            // Hide if all formats fail
                            e.target.style.display = "none";
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
